/**
 * DetailsWidget Component
 *
 * Виджет с детальной информацией об организации.
 * Показывает все основные поля: адрес, контакты, соцсети, описание и т.д.
 * Включает кнопки "Поставить на паузу / Снять с паузы" и "Отказ сотрудничества / Вернуться к сотрудничеству".
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Group,
  Text,
  Stack,
  Anchor,
  Grid,
  Box,
  Badge,
  Button,
  Modal,
  Textarea,
  Checkbox,
  Popover,
  UnstyledButton,
  Loader
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import classes from './DetailsWidget.module.css';
import {
  IconInfoCircle,
  IconMapPin,
  IconPhone,
  IconMail,
  IconWorld,
  IconClock,
  IconBrandVk,
  IconBrandInstagram,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconExternalLink,
  IconBuilding,
  IconPlayerPause,
  IconPlayerPlay,
  IconCheck,
  IconBan,
  IconArrowBackUp,
  IconCalendar,
  IconChevronDown,
  IconTrash
} from '@tabler/icons-react';
import BaseWidget from './BaseWidget';
import tokens from '../../../theme/designTokens';

// Предопределённые причины для паузы/отказа
const PREDEFINED_REASONS = [
  'Не набрался МК',
  'Не интересно',
  'Свой педагог',
  'Не подошли условия',
  'Нет свободного времени в расписании',
];

// Конвертация Date в формат "YYYY-MM"
const formatYearMonth = (date) => {
  if (!date) return undefined;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

// Фолбэк для partnership_status если API не загрузился
const FALLBACK_BADGES = {
  'partner': { color: 'green', label: 'Партнер' },
  'potential_partner': { color: 'blue', label: 'Потенциальный партнер' },
  'previously_cooperated': { color: 'cyan', label: 'Ранее сотрудничали' },
  'low_cost': { color: 'yellow', label: 'Лоу Кост' },
  'high_cost': { color: 'orange', label: 'Хай Кост' },
  'not_interested': { color: 'red', label: 'Не интересен' },
  'gov_schools_gardens': { color: 'gray', label: 'Гос. школы/сады' },
  'gov_children_centers': { color: 'gray', label: 'Гос. детские центры' },
};

/**
 * Компонент для отображения одного поля
 */
const DetailField = ({ label, value, icon: Icon, href, isLink = false }) => {
  if (!value) return null;

  return (
    <Box>
      <Text size="xs" c="dimmed" mb={2}>
        {label}
      </Text>
      <Group gap="xs">
        {Icon && <Icon size={14} style={{ color: tokens.colors.neutral[6] }} />}
        {isLink && href ? (
          <Anchor href={href} target="_blank" size="sm">
            {value}
          </Anchor>
        ) : (
          <Text size="sm">{value}</Text>
        )}
      </Group>
    </Box>
  );
};

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow'
  });
};

/**
 * Popover для смены типа организации (B2B / B2C / Individual)
 * По аналогии со сменой partnership_status
 */
const OrgTypePopover = ({ currentType, currentTypeBadge, typeOptions, organizationId, onOrganizationUpdate }) => {
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = async (newType) => {
    if (newType === currentType) {
      setOpened(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/organizations/${organizationId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_type: newType }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при смене типа');
      }

      const updatedOrg = await response.json();
      const label = typeOptions.find(t => t.value === newType)?.label || newType;

      notifications.show({
        title: 'Тип изменён',
        message: `Тип организации обновлён на «${label}»`,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 3000,
      });

      setOpened(false);
      if (onOrganizationUpdate) {
        onOrganizationUpdate(updatedOrg);
      }
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" width={220} shadow="md" radius="lg" withinPortal>
      <Popover.Target>
        <UnstyledButton onClick={() => setOpened((o) => !o)}>
          <Group gap={4}>
            <Badge color={currentTypeBadge.color} variant="filled" size="lg" radius="xl">
              {currentTypeBadge.label}
            </Badge>
            <IconChevronDown size={14} style={{ color: tokens.colors.neutral[5] }} />
          </Group>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown style={{ borderRadius: 16 }}>
        {loading ? (
          <Box py="sm" style={{ textAlign: 'center' }}>
            <Loader size="sm" />
          </Box>
        ) : (
          <Stack gap={4}>
            {typeOptions.map((t) => (
              <UnstyledButton
                key={t.value}
                onClick={() => handleChange(t.value)}
                className={t.value === currentType ? classes.popoverItemActive : classes.popoverItem}
                style={t.value === currentType
                  ? { backgroundColor: `var(--mantine-color-${t.color}-0)` }
                  : undefined
                }
              >
                <Badge color={t.color} variant="filled" size="sm" radius="xl">
                  {t.label}
                </Badge>
                {t.value === currentType && (
                  <IconCheck size={14} style={{ color: tokens.colors.brand.primary, marginLeft: 'auto' }} />
                )}
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Popover.Dropdown>
    </Popover>
  );
};


const DetailsWidget = ({ organization, onStageChange, onOrganizationUpdate, currentUser }) => {
  const navigate = useNavigate();

  // Загрузка статусов из API
  const [statusesList, setStatusesList] = useState([]);
  const [statusesMap, setStatusesMap] = useState({});
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  const loadStatuses = useCallback(async () => {
    try {
      setStatusesLoading(true);
      const response = await fetch('/api/partnership-statuses', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const map = {};
        data.forEach(s => {
          map[s.code] = { color: s.color, label: s.name };
        });
        setStatusesList(data);
        setStatusesMap(map);
      }
    } catch (err) {
      console.error('Failed to load partnership statuses:', err);
    } finally {
      setStatusesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  // Смена статуса через Popover
  const handleStatusChange = async (newStatusCode) => {
    if (newStatusCode === organization.partnership_status) {
      setStatusPopoverOpen(false);
      return;
    }

    setStatusChangeLoading(true);
    try {
      const response = await fetch(`/api/organizations/${organization.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partnership_status: newStatusCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при смене статуса');
      }

      const updatedOrg = await response.json();

      notifications.show({
        title: 'Статус изменён',
        message: `Статус партнёрства обновлён на «${statusesMap[newStatusCode]?.label || newStatusCode}»`,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 3000,
      });

      setStatusPopoverOpen(false);

      if (onOrganizationUpdate) {
        onOrganizationUpdate(updatedOrg);
      }
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const canChangeStatus = currentUser && ['ADMIN', 'MANAGER', 'SALES'].includes(currentUser.role);

  // Состояние модалки паузы
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [pauseComment, setPauseComment] = useState('');
  const [pauseCommentError, setPauseCommentError] = useState(null);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [pauseReturnDate, setPauseReturnDate] = useState(null);

  // Состояние модалки отказа
  const [refuseModalOpen, setRefuseModalOpen] = useState(false);
  const [refuseComment, setRefuseComment] = useState('');
  const [refuseCommentError, setRefuseCommentError] = useState(null);
  const [refuseLoading, setRefuseLoading] = useState(false);
  const [refuseReasons, setRefuseReasons] = useState([]);
  const [refuseReturnDate, setRefuseReturnDate] = useState(null);

  // Состояние модалки удаления группы компаний
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isPaused = organization.cooperation_stage === 'classes_paused';
  const isRefused = organization.cooperation_stage === 'cooperation_refused';

  // Роли, которые могут ставить на паузу / отказывать
  const canPause = currentUser && ['ADMIN', 'MANAGER', 'SALES'].includes(currentUser.role);
  const canRefuse = currentUser && ['ADMIN', 'MANAGER', 'SALES'].includes(currentUser.role);
  const canDeleteGroup = currentUser?.role === 'ADMIN' && organization.is_company_group;

  const socialIcons = {
    vk: IconBrandVk,
    instagram: IconBrandInstagram,
    telegram: IconBrandTelegram,
    whatsapp: IconBrandWhatsapp,
  };

  // Определяем текущий бейдж: сначала из API, потом fallback
  const badge = statusesMap[organization.partnership_status] ||
    FALLBACK_BADGES[organization.partnership_status] ||
    { color: 'gray', label: organization.partnership_status || 'Не указан' };

  const handlePauseClick = () => {
    setPauseComment('');
    setPauseCommentError(null);
    setPauseReasons([]);
    setPauseReturnDate(null);
    setPauseModalOpen(true);
  };

  const handlePauseConfirm = async () => {
    if (isPaused) {
      // Снятие с паузы — комментарий обязателен
      if (!pauseComment.trim()) {
        setPauseCommentError('Укажите причину снятия с паузы');
        return;
      }
    } else {
      // Постановка на паузу — reasons ИЛИ comment
      const hasReasons = pauseReasons.length > 0;
      const hasComment = pauseComment.trim().length > 0;
      if (!hasReasons && !hasComment) {
        setPauseCommentError('Выберите причину или напишите комментарий');
        return;
      }
    }

    setPauseLoading(true);
    setPauseCommentError(null);

    try {
      const endpoint = isPaused
        ? `/api/organizations/${organization.id}/unpause`
        : `/api/organizations/${organization.id}/pause`;

      // Для unpause — простой формат, для pause — расширенный
      const body = isPaused
        ? { comment: pauseComment.trim() }
        : {
            reasons: pauseReasons.length > 0 ? pauseReasons : undefined,
            comment: pauseComment.trim() || undefined,
            return_date: formatYearMonth(pauseReturnDate),
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при изменении этапа');
      }

      const result = await response.json();

      notifications.show({
        title: isPaused ? 'Снято с паузы' : 'Поставлено на паузу',
        message: result.message,
        color: isPaused ? 'green' : 'orange',
        icon: isPaused ? <IconCheck size={18} /> : <IconPlayerPause size={18} />,
        autoClose: 3000
      });

      setPauseModalOpen(false);

      if (onStageChange) {
        onStageChange(result.new_stage, result.new_stage_name);
      }
    } catch (err) {
      setPauseCommentError(err.message);
    } finally {
      setPauseLoading(false);
    }
  };

  const handleRefuseClick = () => {
    setRefuseComment('');
    setRefuseCommentError(null);
    setRefuseReasons([]);
    setRefuseReturnDate(null);
    setRefuseModalOpen(true);
  };

  const handleRefuseConfirm = async () => {
    if (isRefused) {
      // Возврат к сотрудничеству — комментарий обязателен
      if (!refuseComment.trim()) {
        setRefuseCommentError('Укажите причину возврата к сотрудничеству');
        return;
      }
    } else {
      // Отказ — reasons ИЛИ comment
      const hasReasons = refuseReasons.length > 0;
      const hasComment = refuseComment.trim().length > 0;
      if (!hasReasons && !hasComment) {
        setRefuseCommentError('Выберите причину или напишите комментарий');
        return;
      }
    }

    setRefuseLoading(true);
    setRefuseCommentError(null);

    try {
      const endpoint = isRefused
        ? `/api/organizations/${organization.id}/unrefuse`
        : `/api/organizations/${organization.id}/refuse`;

      // Для unrefuse — простой формат, для refuse — расширенный
      const body = isRefused
        ? { comment: refuseComment.trim() }
        : {
            reasons: refuseReasons.length > 0 ? refuseReasons : undefined,
            comment: refuseComment.trim() || undefined,
            return_date: formatYearMonth(refuseReturnDate),
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при изменении этапа');
      }

      const result = await response.json();

      notifications.show({
        title: isRefused ? 'Возвращено к сотрудничеству' : 'Отказ от сотрудничества',
        message: result.message,
        color: isRefused ? 'green' : 'red',
        icon: isRefused ? <IconCheck size={18} /> : <IconBan size={18} />,
        autoClose: 3000
      });

      setRefuseModalOpen(false);

      if (onStageChange) {
        onStageChange(result.new_stage, result.new_stage_name);
      }
    } catch (err) {
      setRefuseCommentError(err.message);
    } finally {
      setRefuseLoading(false);
    }
  };

  // Удаление группы компаний
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const response = await fetch(`/api/organizations/${organization.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        let message = 'Ошибка при удалении';
        try {
          const errorData = await response.json();
          message = errorData.detail || message;
        } catch { /* ответ не JSON */ }
        throw new Error(message);
      }

      notifications.show({
        title: 'Группа компаний удалена',
        message: `«${organization.name}» и все связанные данные удалены`,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 4000,
      });

      setDeleteModalOpen(false);
      navigate('/organizations');
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <BaseWidget
      title="Детали"
      icon={IconInfoCircle}
      collapsible={false}
      defaultExpanded={true}
    >
      {/* Статус и ссылка на Яндекс */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Статус партнёрства</Text>
          {canChangeStatus ? (
            <Popover
              opened={statusPopoverOpen}
              onChange={setStatusPopoverOpen}
              position="bottom-start"
              width={280}
              shadow="md"
              radius="lg"
              withinPortal
            >
              <Popover.Target>
                <UnstyledButton onClick={() => setStatusPopoverOpen((o) => !o)}>
                  <Group gap={4}>
                    <Badge color={badge.color} variant="filled" size="lg" radius="xl">
                      {badge.label}
                    </Badge>
                    <IconChevronDown size={14} style={{ color: tokens.colors.neutral[5] }} />
                  </Group>
                </UnstyledButton>
              </Popover.Target>
              <Popover.Dropdown style={{ borderRadius: 16 }}>
                {statusesLoading || statusChangeLoading ? (
                  <Box py="sm" style={{ textAlign: 'center' }}>
                    <Loader size="sm" />
                  </Box>
                ) : (
                  <Stack gap={4}>
                    {statusesList.map((s) => (
                      <UnstyledButton
                        key={s.code}
                        onClick={() => handleStatusChange(s.code)}
                        className={s.code === organization.partnership_status ? classes.popoverItemActive : classes.popoverItem}
                        style={s.code === organization.partnership_status
                          ? { backgroundColor: `var(--mantine-color-${s.color}-0)` }
                          : undefined
                        }
                      >
                        <Badge color={s.color} variant="filled" size="sm" radius="xl">
                          {s.name}
                        </Badge>
                        {s.code === organization.partnership_status && (
                          <IconCheck size={14} style={{ color: tokens.colors.brand.primary, marginLeft: 'auto' }} />
                        )}
                      </UnstyledButton>
                    ))}
                  </Stack>
                )}
              </Popover.Dropdown>
            </Popover>
          ) : (
            <Badge color={badge.color} variant="filled" size="lg" radius="xl">
              {badge.label}
            </Badge>
          )}
        </Box>

        {organization.yandex_url && (
          <Anchor
            href={organization.yandex_url}
            target="_blank"
            size="sm"
          >
            <Group gap={4}>
              <IconExternalLink size={14} />
              Открыть на Яндекс.Картах
            </Group>
          </Anchor>
        )}
      </Group>

      {/* Тип организации */}
      <Box mb="lg">
        <Text size="xs" c="dimmed" mb={4}>Тип организации</Text>
        {(() => {
          const typeConfig = {
            b2b: { color: 'blue', label: 'B2B' },
            b2c: { color: 'green', label: 'B2C' },
            individual: { color: 'orange', label: 'Индивидуальные' },
          };
          const typeOptions = [
            { value: 'b2b', color: 'blue', label: 'B2B' },
            { value: 'b2c', color: 'green', label: 'B2C' },
            { value: 'individual', color: 'orange', label: 'Индивидуальные' },
          ];
          const currentType = organization.organization_type;
          const currentTypeBadge = typeConfig[currentType] || { color: 'gray', label: 'Не указан' };
          const isIndividual = currentType === 'individual';
          const canChangeType = canChangeStatus && !isIndividual;

          if (canChangeType) {
            return (
              <OrgTypePopover
                currentType={currentType}
                currentTypeBadge={currentTypeBadge}
                typeOptions={typeOptions}
                organizationId={organization.id}
                onOrganizationUpdate={onOrganizationUpdate}
              />
            );
          }
          return (
            <Badge color={currentTypeBadge.color} variant="filled" size="lg" radius="xl">
              {currentTypeBadge.label}
            </Badge>
          );
        })()}
      </Box>

      {/* Кнопки паузы, отказа и удаления */}
      {(canPause || canRefuse || canDeleteGroup) && (
        <Stack gap="xs" mb="lg">
          {/* Кнопка паузы */}
          {canPause && (
            isPaused ? (
              <Button
                variant="light"
                color="green"
                size="sm"
                fullWidth
                leftSection={<IconPlayerPlay size={16} />}
                onClick={handlePauseClick}
              >
                Снять с паузы
              </Button>
            ) : (
              <Button
                variant="light"
                color="orange"
                size="sm"
                fullWidth
                leftSection={<IconPlayerPause size={16} />}
                onClick={handlePauseClick}
              >
                Поставить на паузу
              </Button>
            )
          )}

          {/* Кнопка отказа */}
          {canRefuse && (
            isRefused ? (
              <Button
                variant="light"
                color="green"
                size="sm"
                fullWidth
                leftSection={<IconArrowBackUp size={16} />}
                onClick={handleRefuseClick}
              >
                Вернуться к сотрудничеству
              </Button>
            ) : (
              <Button
                variant="light"
                color="grape"
                size="sm"
                fullWidth
                leftSection={<IconBan size={16} />}
                onClick={handleRefuseClick}
                style={{ color: '#800020', borderColor: '#800020' }}
                styles={{ root: { '&:hover': { backgroundColor: 'rgba(128, 0, 32, 0.08)' } } }}
              >
                Отказ сотрудничества
              </Button>
            )
          )}

          {/* Кнопка удаления группы компаний (только ADMIN) */}
          {canDeleteGroup && (
            <Button
              variant="light"
              color="red"
              size="sm"
              fullWidth
              leftSection={<IconTrash size={16} />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Удалить группу компаний
            </Button>
          )}
        </Stack>
      )}

      <Grid gutter="lg">
        {/* Левая колонка */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Stack gap="md">
            <DetailField
              label="Адрес"
              value={organization.address}
              icon={IconMapPin}
            />

            <DetailField
              label="Район"
              value={organization.district_name}
            />

            <DetailField
              label="Категория"
              value={organization.category}
            />

            {/* Входит в группу компаний */}
            {organization.company_group_id && organization.company_group_name && (
              <Box>
                <Text size="xs" c="dimmed" mb={2}>
                  Входит в группу компаний
                </Text>
                <Group gap="xs">
                  <IconBuilding size={14} style={{ color: tokens.colors.brand.primary }} />
                  <Anchor
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/organizations/${organization.company_group_id}`);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {organization.company_group_name}
                  </Anchor>
                </Group>
              </Box>
            )}

            <DetailField
              label="Часы работы"
              value={organization.working_hours}
              icon={IconClock}
            />
          </Stack>
        </Grid.Col>

        {/* Правая колонка */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Stack gap="md">
            {/* Телефоны */}
            {organization.phones?.length > 0 && (
              <Box>
                <Text size="xs" c="dimmed" mb={2}>Телефоны</Text>
                <Stack gap={4}>
                  {organization.phones.map((phone, index) => (
                    <Group gap="xs" key={index}>
                      <IconPhone size={14} style={{ color: tokens.colors.neutral[6] }} />
                      <Anchor href={`tel:${phone}`} size="sm">
                        {phone}
                      </Anchor>
                    </Group>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Email-ы */}
            {organization.emails?.length > 0 && (
              <Box>
                <Text size="xs" c="dimmed" mb={2}>Email</Text>
                <Stack gap={4}>
                  {organization.emails.map((email, index) => (
                    <Group gap="xs" key={index}>
                      <IconMail size={14} style={{ color: tokens.colors.neutral[6] }} />
                      <Anchor href={`mailto:${email}`} size="sm">
                        {email}
                      </Anchor>
                    </Group>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Сайт */}
            <DetailField
              label="Сайт"
              value={organization.website}
              icon={IconWorld}
              href={organization.website}
              isLink={true}
            />

            {/* Социальные сети */}
            {organization.social_media && Object.keys(organization.social_media).length > 0 && (
              <Box>
                <Text size="xs" c="dimmed" mb={2}>Социальные сети</Text>
                <Group gap="xs">
                  {Object.entries(organization.social_media).map(([network, url]) => {
                    const SocialIcon = socialIcons[network] || IconWorld;
                    return (
                      <Anchor
                        key={network}
                        href={url}
                        target="_blank"
                        title={network}
                      >
                        <Badge
                          variant="light"
                          leftSection={<SocialIcon size={12} />}
                          size="sm"
                        >
                          {network}
                        </Badge>
                      </Anchor>
                    );
                  })}
                </Group>
              </Box>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Описание */}
      {organization.description && (
        <Box mt="md" pt="md" style={{ borderTop: `1px solid ${tokens.colors.neutral[3]}` }}>
          <Text size="xs" c="dimmed" mb={4}>Описание</Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {organization.description}
          </Text>
        </Box>
      )}

      {/* Метаданные */}
      <Box mt="md" pt="md" style={{ borderTop: `1px solid ${tokens.colors.neutral[3]}` }}>
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="xs" c="dimmed">Создано</Text>
            <Text size="sm">{formatDate(organization.created_at)}</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="xs" c="dimmed">Обновлено</Text>
            <Text size="sm">{formatDate(organization.updated_at)}</Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="xs" c="dimmed">Синхронизировано</Text>
            <Text size="sm">{formatDate(organization.last_synced_at) || '—'}</Text>
          </Grid.Col>
        </Grid>
      </Box>

      {/* Модалка паузы */}
      <Modal
        opened={pauseModalOpen}
        onClose={() => setPauseModalOpen(false)}
        title={isPaused ? 'Снять с паузы' : 'Поставить на паузу'}
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {isPaused
              ? 'Организация будет возвращена на этап, с которого была поставлена на паузу. Укажите причину снятия с паузы.'
              : 'Организация будет переведена на этап «Приостановка». Выберите причину или напишите комментарий.'
            }
          </Text>

          {/* Чекбоксы причин (только при постановке на паузу) */}
          {!isPaused && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>Причина</Text>
              {PREDEFINED_REASONS.map((reason) => (
                <Checkbox
                  key={reason}
                  label={reason}
                  size="sm"
                  radius="xl"
                  checked={pauseReasons.includes(reason)}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setPauseReasons([...pauseReasons, reason]);
                    } else {
                      setPauseReasons(pauseReasons.filter(r => r !== reason));
                    }
                    if (pauseCommentError) setPauseCommentError(null);
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Дата возврата (только при постановке на паузу) */}
          {!isPaused && (
            <MonthPickerInput
              label="Вернуться к организации"
              placeholder="Выберите месяц и год"
              value={pauseReturnDate}
              onChange={setPauseReturnDate}
              leftSection={<IconCalendar size={16} />}
              locale="ru"
              clearable
              radius="xl"
              minDate={new Date()}
            />
          )}

          <Textarea
            label={isPaused ? 'Причина снятия с паузы' : 'Комментарий'}
            placeholder={isPaused ? 'Опишите причину снятия с паузы...' : 'Дополнительный комментарий...'}
            value={pauseComment}
            onChange={(e) => {
              setPauseComment(e.currentTarget.value);
              if (pauseCommentError) setPauseCommentError(null);
            }}
            error={pauseCommentError}
            minRows={3}
            autosize
            radius="xl"
            required={isPaused}
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setPauseModalOpen(false)}
            >
              Отмена
            </Button>
            <Button
              color={isPaused ? 'green' : 'orange'}
              onClick={handlePauseConfirm}
              loading={pauseLoading}
            >
              Подтвердить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Модалка отказа */}
      <Modal
        opened={refuseModalOpen}
        onClose={() => setRefuseModalOpen(false)}
        title={isRefused ? 'Вернуться к сотрудничеству' : 'Отказ от сотрудничества'}
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {isRefused
              ? 'Организация будет возвращена на этап, с которого был оформлен отказ. Укажите причину возврата.'
              : 'Организация будет переведена на этап «Отказ». Выберите причину или напишите комментарий.'
            }
          </Text>

          {/* Чекбоксы причин (только при отказе) */}
          {!isRefused && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>Причина</Text>
              {PREDEFINED_REASONS.map((reason) => (
                <Checkbox
                  key={reason}
                  label={reason}
                  size="sm"
                  radius="xl"
                  checked={refuseReasons.includes(reason)}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setRefuseReasons([...refuseReasons, reason]);
                    } else {
                      setRefuseReasons(refuseReasons.filter(r => r !== reason));
                    }
                    if (refuseCommentError) setRefuseCommentError(null);
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Дата возврата (только при отказе) */}
          {!isRefused && (
            <MonthPickerInput
              label="Вернуться к организации"
              placeholder="Выберите месяц и год"
              value={refuseReturnDate}
              onChange={setRefuseReturnDate}
              leftSection={<IconCalendar size={16} />}
              locale="ru"
              clearable
              radius="xl"
              minDate={new Date()}
            />
          )}

          <Textarea
            label={isRefused ? 'Причина возврата к сотрудничеству' : 'Комментарий'}
            placeholder={isRefused ? 'Опишите причину возврата к сотрудничеству...' : 'Дополнительный комментарий...'}
            value={refuseComment}
            onChange={(e) => {
              setRefuseComment(e.currentTarget.value);
              if (refuseCommentError) setRefuseCommentError(null);
            }}
            error={refuseCommentError}
            minRows={3}
            autosize
            radius="xl"
            required={isRefused}
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setRefuseModalOpen(false)}
            >
              Отмена
            </Button>
            <Button
              color={isRefused ? 'green' : 'red'}
              onClick={handleRefuseConfirm}
              loading={refuseLoading}
            >
              Подтвердить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Модалка удаления группы компаний */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Удалить группу компаний"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Вы собираетесь удалить группу компаний <Text span fw={600}>«{organization.name}»</Text>. Это действие необратимо.
          </Text>

          <Box
            p="sm"
            style={{
              backgroundColor: '#FFF5F5',
              borderRadius: 12,
              border: `1px solid ${tokens.colors.semantic.error.primary}30`,
            }}
          >
            <Text size="sm" fw={500} c="red" mb={4}>Будут удалены:</Text>
            <Text size="sm" c="dimmed">• Все контакты организации</Text>
            <Text size="sm" c="dimmed">• Все звонки и встречи</Text>
            <Text size="sm" c="dimmed">• Все сообщения</Text>
            <Text size="sm" c="dimmed">• Все реквизиты и договоры</Text>
            <Text size="sm" c="dimmed">• История изменений</Text>
          </Box>

          <Text size="sm" c="dimmed">
            Организации, входящие в группу, будут отвязаны, но не удалены.
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setDeleteModalOpen(false)}
            >
              Отмена
            </Button>
            <Button
              color="red"
              onClick={handleDeleteConfirm}
              loading={deleteLoading}
              leftSection={<IconTrash size={16} />}
            >
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </BaseWidget>
  );
};

export default DetailsWidget;
